import {
  Cloud,
  Cpu,
  ListOrdered,
  RefreshCw,
  Server,
  TriangleAlert,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { CardSkeleton, SectionCard } from "../dashboard/DashboardPrimitives";
import { JOB_STATUS_VARIANT, STATE_VARIANT } from "./badges";
import { ClipsOverTimeCard } from "./ClipsOverTimeCard";
import { ClipsTable } from "./ClipsTable";
import { RenderCapacitySection } from "./RenderCapacitySection";
import {
  certDaysLeft,
  describeJobStatus,
  formatAge,
  formatDuration,
  formatUpdated,
  formatUptime,
  planeLabel,
  summarizeFleet,
  workerAttention,
  type FleetSummary,
} from "./fleetSummary";
import { formatShare, selfHostedShare, windowLabel } from "./fleetWindow";
import {
  DEFAULT_WINDOW_HOURS,
  describeWorkerState,
  type H3Fleet,
  type H3History,
  type H3Job,
  type H3Jobs,
  type H3WorkerHistory,
  type RunPodPod,
  type SelfHostedWorker,
  useH3Fleet,
} from "./h3Fleet";
import { useNow } from "./useNow";
import { WindowToggle } from "./WindowToggle";
import { WorkerStatsCards } from "./WorkerStatsCards";

// The GPU Fleet page: every GPU this environment can render H3 clips on —
// self-hosted workers pulling from the pool queue (the registry rows they
// heartbeat into the H3 work bucket), the pool queue's depth, and the RunPod
// Serverless endpoint's health. Read-only: the levers (pause file, compose down,
// RunPod min/max) live on the boxes and in declared infra, on purpose.

const REFRESH_MS = 30 * 1000;

const RUNPOD_STATE_LABEL: Record<string, string> = {
  idle: "idle",
  ready: "ready",
  running: "running",
  initializing: "booting",
  throttled: "throttled",
  unhealthy: "unhealthy",
};

const RUNPOD_JOB_LABEL: Record<string, string> = {
  inQueue: "in queue",
  inProgress: "in progress",
  completed: "completed",
  failed: "failed",
  retried: "retried",
};

type Tone = "neutral" | "good" | "warn" | "bad";

function Tile({
  label,
  value,
  sublabel,
  tone = "neutral",
}: {
  label: string;
  value: ReactNode;
  sublabel?: ReactNode;
  tone?: Tone;
}) {
  return (
    <div
      className={cn(
        "flex min-h-28 flex-col justify-between rounded-xl border bg-card p-4 text-card-foreground shadow-xs",
        tone === "bad" && "border-destructive/50",
        tone === "warn" && "border-amber-500/50",
      )}
    >
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div className="space-y-1 pt-2">
        <span
          className={cn(
            "text-3xl font-semibold leading-none tracking-tight tabular-nums",
            tone === "bad" && "text-destructive",
            tone === "good" && "text-emerald-700 dark:text-emerald-400",
          )}
        >
          {value}
        </span>
        {sublabel ? (
          <p className="text-xs text-muted-foreground">{sublabel}</p>
        ) : null}
      </div>
    </div>
  );
}

function Tiles({
  fleet,
  summary,
  history,
}: {
  fleet: H3Fleet;
  summary: FleetSummary;
  history: H3History | null;
}) {
  const rp = summary.runpod;
  const pool = fleet.pool_queue ?? null;
  const problems = summary.attention + (summary.poolDlq ?? 0);
  const historyKnown = Boolean(history && !history.error);
  const share = selfHostedShare(history?.by_plane);
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <Tile
        label={`Delivered, last ${windowLabel(history?.window_hours ?? DEFAULT_WINDOW_HOURS)}`}
        value={historyKnown ? (history?.completed ?? 0) : "—"}
        tone={share !== null && share >= 0.5 ? "good" : "neutral"}
        sublabel={
          historyKnown
            ? `${share === null ? "—" : formatShare(share)} on your GPUs · ${history?.failed ?? 0} failed`
            : "history unavailable"
        }
      />
      <Tile
        label="Self-hosted online"
        value={summary.online}
        tone={summary.online > 0 ? "good" : "neutral"}
        sublabel={`${summary.free} free now · ${summary.rendering} rendering`}
      />
      <Tile
        label="RunPod workers"
        // RunPod's `ready` overlaps `idle` (it is not a separate worker), so the
        // total is the distinct states; throttled workers exist but have no GPU yet.
        value={
          rp
            ? rp.idle + rp.running + rp.booting + rp.throttled + rp.unhealthy
            : "—"
        }
        sublabel={
          rp
            ? `${rp.idle} idle · ${rp.running} running · ${rp.booting} booting${
                rp.throttled ? ` · ${rp.throttled} throttled (no GPU yet)` : ""
              }${rp.unhealthy ? ` · ${rp.unhealthy} unhealthy` : ""}`
            : "endpoint not bound in this environment"
        }
      />
      <Tile
        label="Waiting for a GPU"
        value={summary.waiting}
        tone={summary.waiting > 0 ? "warn" : "neutral"}
        sublabel={
          pool
            ? `${pool.visible} in the pool queue · ${rp?.inQueue ?? 0} on RunPod`
            : `${rp?.inQueue ?? 0} on RunPod · pool queue not bound`
        }
      />
      <Tile
        label="Needs attention"
        value={problems}
        tone={problems > 0 ? "bad" : "good"}
        sublabel={
          problems > 0
            ? `${summary.attention} worker${summary.attention === 1 ? "" : "s"}${
                summary.poolDlq
                  ? ` · ${summary.poolDlq} poison envelope${summary.poolDlq === 1 ? "" : "s"}`
                  : ""
              }`
            : "every worker is healthy"
        }
      />
    </div>
  );
}

function WorkerRow({
  worker,
  stats,
  now,
}: {
  worker: SelfHostedWorker;
  stats: H3WorkerHistory | null;
  now: Date;
}) {
  const attention = workerAttention(worker, now);
  const certDays = certDaysLeft(worker.cert_not_after, now);
  const where = [worker.location, worker.host].filter(Boolean).join(" · ");
  return (
    <TableRow className={attention ? "bg-destructive/5" : undefined}>
      <TableCell className="font-mono text-xs">
        {worker.worker_id}
        {where ? (
          <span className="block text-muted-foreground">{where}</span>
        ) : null}
      </TableCell>
      <TableCell>
        <Badge
          variant={STATE_VARIANT[worker.state] ?? "outline"}
          title={describeWorkerState(worker.state)}
        >
          {worker.state}
        </Badge>
        <span className="block pt-1 text-xs text-muted-foreground">
          {describeWorkerState(worker.state)}
        </span>
        {attention ? (
          <span className="block pt-1 text-xs text-destructive">
            <TriangleAlert aria-hidden className="mr-1 inline size-3" />
            {attention}
          </span>
        ) : null}
      </TableCell>
      <TableCell className="text-xs">
        {worker.gpu ?? "—"}
        {worker.vram_gib ? (
          <span className="block text-muted-foreground">
            {worker.vram_gib} GiB · {worker.profile ?? "?"}
          </span>
        ) : null}
      </TableCell>
      <TableCell
        className="font-mono text-xs"
        title={worker.current_job_id ?? undefined}
      >
        {worker.current_job_id ? worker.current_job_id.slice(0, 8) : "—"}
      </TableCell>
      <TableCell className="text-xs">
        {stats ? (
          <span
            className="block tabular-nums"
            title="Clips finished in the selected window, from the composer's supervisor events"
          >
            {stats.clips} in window
          </span>
        ) : null}
        <span
          className="block text-muted-foreground"
          title="The worker process's own counters; they restart with its container"
        >
          {worker.jobs_completed ?? 0} ok
          {worker.jobs_failed ? ` · ${worker.jobs_failed} failed` : ""} since
          start
        </span>
        {worker.consecutive_failures ? (
          <span className="block text-muted-foreground">
            {worker.consecutive_failures} in a row
          </span>
        ) : null}
      </TableCell>
      <TableCell
        className="font-mono text-xs"
        title={worker.image_digest ?? undefined}
      >
        {worker.image_digest ? worker.image_digest.slice(7, 19) : "—"}
        {worker.comfy_commit ? (
          <span className="block text-muted-foreground">
            comfy {worker.comfy_commit.slice(0, 8)}
          </span>
        ) : null}
      </TableCell>
      <TableCell className="text-xs">
        {formatUptime(worker.started_at, now)}
      </TableCell>
      <TableCell
        className={cn(
          "text-xs",
          certDays !== null && certDays <= 30 && "text-destructive",
        )}
        title={worker.cert_not_after ?? undefined}
      >
        {certDays === null ? "—" : certDays < 0 ? "expired" : `${certDays} d`}
      </TableCell>
      <TableCell className="text-xs">
        {formatAge(worker.last_seen_seconds_ago)}
      </TableCell>
    </TableRow>
  );
}

function WorkersSection({
  fleet,
  history,
  now,
}: {
  fleet: H3Fleet;
  history: H3History | null;
  now: Date;
}) {
  const { workers } = fleet.self_hosted;
  const statsFor = (workerId: string) =>
    history?.by_worker?.find(
      (row) => row.plane === "self_hosted" && row.worker_id === workerId,
    ) ?? null;
  return (
    <SectionCard
      title="Self-hosted workers"
      icon={<Server className="size-4" />}
      action={
        <span className="text-xs text-muted-foreground">
          registry in{" "}
          <code className="font-mono">{fleet.work_bucket}/workers/</code>
        </span>
      }
    >
      {workers.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No self-hosted worker has enrolled in this environment. Every H3 clip
          renders on RunPod. Enrol a box with the self-hosted kit
          (echoflicks-infra{" "}
          <code className="font-mono">packer/h3-worker/self-hosted/</code>).
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Worker</TableHead>
              <TableHead>State</TableHead>
              <TableHead>GPU</TableHead>
              <TableHead>Job</TableHead>
              <TableHead>Clips</TableHead>
              <TableHead>Image</TableHead>
              <TableHead>Uptime</TableHead>
              <TableHead>Cert</TableHead>
              <TableHead>Last seen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {workers.map((worker) => (
              <WorkerRow
                key={worker.worker_id}
                worker={worker}
                stats={statsFor(worker.worker_id)}
                now={now}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  );
}

function RunPodSection({ runpod }: { runpod: NonNullable<H3Fleet["runpod"]> }) {
  return (
    <SectionCard
      title="RunPod Serverless endpoint"
      icon={<Cloud className="size-4" />}
      action={
        <span className="font-mono text-xs text-muted-foreground">
          {runpod.endpoint_id}
        </span>
      }
    >
      <div className="flex flex-wrap gap-2">
        {Object.entries(runpod.workers).map(([state, count]) => (
          <Badge
            key={state}
            variant={
              count > 0
                ? state === "unhealthy" || state === "throttled"
                  ? "destructive"
                  : "secondary"
                : "outline"
            }
          >
            {count} {RUNPOD_STATE_LABEL[state] ?? state}
          </Badge>
        ))}
      </div>
      {Object.keys(runpod.jobs).length > 0 ? (
        <dl className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-5">
          {Object.entries(runpod.jobs).map(([state, count]) => (
            <div key={state}>
              <dt className="text-xs text-muted-foreground">
                {RUNPOD_JOB_LABEL[state] ?? state}
              </dt>
              <dd className="tabular-nums">{count}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      <p className="mt-3 text-xs text-muted-foreground">
        RunPod has no per-worker identity API, so its workers appear as counts
        per state, from the endpoint&apos;s health check (cached 30 s). The
        composer treats the endpoint as cold when nothing is idle or running.
      </p>
    </SectionCard>
  );
}

function PoolQueueSection({
  pool,
}: {
  pool: NonNullable<H3Fleet["pool_queue"]>;
}) {
  const stats: Array<[string, ReactNode, string, boolean]> = [
    ["Waiting", pool.visible, "Envelopes no worker has pulled yet", false],
    [
      "In flight",
      pool.in_flight,
      "Pulled by a worker and being rendered (inside the visibility timeout)",
      false,
    ],
    ["Delayed", pool.delayed, "Not yet visible to workers", false],
    [
      "Poison (DLQ)",
      pool.dlq ?? "—",
      "Envelopes received three times without success",
      (pool.dlq ?? 0) > 0,
    ],
  ];
  return (
    <SectionCard
      title="Self-hosted pool queue"
      icon={<ListOrdered className="size-4" />}
      action={
        <span className="font-mono text-xs text-muted-foreground">
          {pool.queue}
        </span>
      }
    >
      <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        {stats.map(([label, value, hint, bad]) => (
          <div key={label} title={hint}>
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd
              className={cn(
                "text-xl font-semibold tabular-nums",
                bad && "text-destructive",
              )}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>
    </SectionCard>
  );
}

function PodsSection({
  pods,
  templateImage,
}: {
  pods: RunPodPod[];
  templateImage: string | null | undefined;
}) {
  return (
    <SectionCard
      title="RunPod pods"
      icon={<Cloud className="size-4" />}
      action={
        <span className="text-xs text-muted-foreground">
          template image{" "}
          <code className="font-mono">
            {templateImage ? templateImage.slice(7, 19) : "—"}
          </code>
        </span>
      }
    >
      {pods.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          RunPod holds no pod for this endpoint right now.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Pod</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>GPU</TableHead>
              <TableHead>Image</TableHead>
              <TableHead>Load</TableHead>
              <TableHead>Uptime</TableHead>
              <TableHead>$/h</TableHead>
              <TableHead>Last change</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pods.map((pod) => (
              <TableRow
                key={pod.id}
                className={pod.status !== "RUNNING" ? "opacity-60" : undefined}
              >
                <TableCell className="font-mono text-xs">{pod.id}</TableCell>
                <TableCell>
                  <Badge
                    variant={pod.status === "RUNNING" ? "default" : "outline"}
                  >
                    {pod.status.toLowerCase()}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs">
                  {pod.gpu ?? "—"}
                  {pod.location ? (
                    <span className="block text-muted-foreground">
                      {pod.location}
                    </span>
                  ) : null}
                </TableCell>
                <TableCell
                  className="font-mono text-xs"
                  title={pod.image_digest ?? undefined}
                >
                  {pod.image_digest ? pod.image_digest.slice(7, 19) : "—"}
                  <span
                    className={cn(
                      "block font-sans",
                      pod.image_current
                        ? "text-muted-foreground"
                        : "text-destructive",
                    )}
                  >
                    {pod.image_current ? "current" : "old image"}
                  </span>
                </TableCell>
                <TableCell className="text-xs tabular-nums">
                  {pod.gpu_util_percent === null ||
                  pod.gpu_util_percent === undefined
                    ? "—"
                    : `${pod.gpu_util_percent}% gpu · ${pod.memory_util_percent ?? 0}% mem`}
                </TableCell>
                <TableCell className="text-xs">
                  {formatDuration(pod.uptime_seconds)}
                </TableCell>
                <TableCell className="text-xs tabular-nums">
                  {pod.cost_per_hour === null || pod.cost_per_hour === undefined
                    ? "—"
                    : pod.cost_per_hour.toFixed(2)}
                </TableCell>
                <TableCell className="text-xs">
                  {pod.last_change ?? "—"}
                  {pod.since ? (
                    <span className="block text-muted-foreground">
                      {formatAge((Date.now() - Date.parse(pod.since)) / 1000)}
                    </span>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        From RunPod&apos;s pod list, not its health counts: a pod can report
        &quot;running&quot; while it loads weights or waits for a GPU on its
        host, and a resumed pod keeps the image it was created with.
      </p>
    </SectionCard>
  );
}

function JobRows({ jobs, now }: { jobs: H3Job[]; now: Date }) {
  return (
    <>
      {jobs.map((job) => (
        <TableRow key={job.job_id}>
          <TableCell className="font-mono text-xs" title={job.job_id}>
            {job.job_id.slice(0, 8)}
          </TableCell>
          <TableCell>
            <Badge variant={JOB_STATUS_VARIANT[job.status]}>
              {job.status.replace("_", " ")}
            </Badge>
            <span className="block pt-1 text-xs text-muted-foreground">
              {describeJobStatus(job)}
            </span>
          </TableCell>
          <TableCell className="text-xs">
            {planeLabel(job.plane)}
            {job.worker_id ? (
              <span className="block font-mono text-muted-foreground">
                {job.worker_id}
              </span>
            ) : null}
          </TableCell>
          <TableCell className="text-xs tabular-nums">
            {job.attempt ?? 1}
            {job.redispatched ? ` · re-posted ${job.redispatched}×` : ""}
            {job.lost ? ` · lost ${job.lost}×` : ""}
          </TableCell>
          <TableCell className="text-xs tabular-nums">
            {job.gen_seconds !== null
              ? formatDuration(job.gen_seconds)
              : formatDuration(
                  (now.getTime() - Date.parse(job.dispatched_at)) / 1000,
                )}
            {job.gpu ? (
              <span className="block text-muted-foreground">{job.gpu}</span>
            ) : null}
          </TableCell>
          <TableCell className="text-xs">
            {formatAge((now.getTime() - Date.parse(job.dispatched_at)) / 1000)}
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

function JobsTable({ jobs, now }: { jobs: H3Job[]; now: Date }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Clip</TableHead>
          <TableHead>State</TableHead>
          <TableHead>Plane · worker</TableHead>
          <TableHead>Attempt</TableHead>
          <TableHead>Gen / elapsed</TableHead>
          <TableHead>Dispatched</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <JobRows jobs={jobs} now={now} />
      </TableBody>
    </Table>
  );
}

function JobsSection({
  jobs,
  history,
  now,
}: {
  jobs: H3Jobs;
  history: H3History | null;
  now: Date;
}) {
  // The fan-out follows the selected window when the API served a history; an
  // older API, or a failed history query, falls back to the live slice.
  const windowed =
    history && !history.error && history.by_plane ? history : null;
  const byPlane = windowed?.by_plane ?? jobs.by_plane;
  const label = windowed
    ? `last ${windowLabel(windowed.window_hours)}`
    : `last ${jobs.window_hours} h`;
  const total = windowed ? (windowed.total ?? 0) : jobs.total;
  const planes = Object.entries(byPlane).sort(
    ([, a], [, b]) => b.jobs - a.jobs,
  );
  return (
    <>
      <SectionCard
        title={`Fan-out, ${label}`}
        icon={<ListOrdered className="size-4" />}
        action={
          <span className="text-xs text-muted-foreground">
            {total} clip{total === 1 ? "" : "s"} · {jobs.in_flight.length} in
            flight now
          </span>
        }
      >
        {planes.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No H3 clip finished in this environment in the {label}.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plane</TableHead>
                <TableHead>Clips</TableHead>
                <TableHead>Delivered</TableHead>
                <TableHead>Failed</TableHead>
                <TableHead>In flight</TableHead>
                <TableHead>Unsupervised</TableHead>
                <TableHead>Re-posted</TableHead>
                <TableHead>Lost</TableHead>
                <TableHead>Avg gen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {planes.map(([plane, totals]) => (
                <TableRow key={plane}>
                  <TableCell className="text-sm">{planeLabel(plane)}</TableCell>
                  <TableCell className="tabular-nums">{totals.jobs}</TableCell>
                  <TableCell className="tabular-nums">
                    {totals.completed}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "tabular-nums",
                      totals.failed > 0 && "text-destructive",
                    )}
                  >
                    {totals.failed}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {totals.in_flight}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {totals.unsupervised ?? 0}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {totals.redispatched}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "tabular-nums",
                      totals.lost > 0 && "text-destructive",
                    )}
                  >
                    {totals.lost}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {formatDuration(totals.avg_gen_seconds)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          From the composer&apos;s supervisor events. A clip is re-posted when
          nobody claims it by the deadline or its worker stops heartbeating; a
          supervised clip with no outcome after an hour shows as unknown. A clip
          posted on the old v1 envelope is unsupervised: RunPod tracks it and no
          outcome is logged here.
        </p>
      </SectionCard>

      <SectionCard
        title="Clips in flight"
        icon={<Server className="size-4" />}
        action={
          <span className="text-xs text-muted-foreground">
            {jobs.in_flight.length} now
          </span>
        }
      >
        {jobs.in_flight.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing rendering.</p>
        ) : (
          <JobsTable jobs={jobs.in_flight} now={now} />
        )}
      </SectionCard>

      {windowed ? (
        <ClipsTable history={windowed} now={now} />
      ) : jobs.recent.length > 0 ? (
        <SectionCard
          title="Recent clips"
          icon={<ListOrdered className="size-4" />}
          action={
            <span className="text-xs text-muted-foreground">
              last {jobs.recent.length}
            </span>
          }
        >
          <JobsTable jobs={jobs.recent} now={now} />
        </SectionCard>
      ) : null}
    </>
  );
}

function UnavailableCard({ error }: { error: Error | null }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <TriangleAlert aria-hidden className="size-4 text-destructive" />
          Fleet status is unavailable
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm text-muted-foreground">
        <p>
          The dashboard API did not answer{" "}
          <code className="font-mono">/config/gpu-fleet</code> (or{" "}
          <code className="font-mono">/config/h3-fleet</code> on an older one)
          {error ? ` (${error.message})` : ""}.
        </p>
        <ul className="list-disc pl-4 text-xs">
          <li>
            503 — this environment has no H3 work bucket bound to the dashboard
            API.
          </li>
          <li>401 — the CRM session has expired; sign in again.</li>
          <li>404 — the deployed dashboard API predates the fleet route.</li>
        </ul>
      </CardContent>
    </Card>
  );
}

export const GpuFleetPage = () => {
  const [windowHours, setWindowHours] = useState(DEFAULT_WINDOW_HOURS);
  const {
    fleet,
    isLoading,
    isFetching,
    isPreviousWindow,
    error,
    refetch,
    dataUpdatedAt,
  } = useH3Fleet({ refetchIntervalMs: REFRESH_MS, windowHours });
  // A ticking clock: elapsed times move between the 30 s refetches.
  const now = useNow(1000);
  const summary = fleet ? summarizeFleet(fleet, now) : null;
  const history = fleet?.jobs?.history ?? null;
  return (
    <div className="space-y-6 p-6">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Cpu className="size-5 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">GPU Fleet</h1>
          {fleet ? (
            <Badge
              variant="outline"
              title="Environment the dashboard API serves"
            >
              {fleet.environment}
            </Badge>
          ) : null}
          {fleet ? (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span
                aria-hidden
                className={cn(
                  "size-2 rounded-full bg-emerald-500",
                  isFetching && "animate-pulse",
                )}
              />
              live · updated {formatUpdated(dataUpdatedAt, now.getTime())}
            </span>
          ) : null}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <WindowToggle value={windowHours} onChange={setWindowHours} />
            <Button
              variant="outline"
              size="sm"
              onClick={refetch}
              disabled={isFetching}
              title="Re-read the registry, the queue, RunPod and the logs now (bypasses the 30 s cache)"
            >
              <RefreshCw
                aria-hidden
                className={cn("size-4", isFetching && "animate-spin")}
              />
              Refresh
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Every GPU this environment can render H3 clips on: self-hosted workers
          pulling from the pool queue (your own boxes) and the RunPod Serverless
          endpoint. The composer hands each clip to the first idle self-hosted
          worker, then to RunPod. Refreshes every 30 s; the window picks which
          finished clips the chart, the GPU cards and the clip list cover. Which
          provider gets a scene in the first place is decided in{" "}
          <Link to="/ai-settings" className="underline underline-offset-4">
            AI Settings
          </Link>
          .
        </p>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="pt-6">
            <CardSkeleton rows={6} />
          </CardContent>
        </Card>
      ) : fleet && summary ? (
        <div
          className={cn(
            "space-y-6 transition-opacity",
            isPreviousWindow && "opacity-60",
          )}
        >
          <Tiles fleet={fleet} summary={summary} history={history} />
          {fleet.render_capacity ? (
            <RenderCapacitySection
              capacity={fleet.render_capacity}
              asOf={fleet.as_of}
            />
          ) : null}
          <WorkerStatsCards
            fleet={fleet}
            history={history}
            inFlight={fleet.jobs?.in_flight ?? []}
            now={now}
            windowHours={windowHours}
          />
          {history && !history.error ? (
            <ClipsOverTimeCard history={history} />
          ) : null}
          {fleet.jobs ? (
            <JobsSection jobs={fleet.jobs} history={history} now={now} />
          ) : null}
          <WorkersSection fleet={fleet} history={history} now={now} />
          {fleet.runpod?.pods ? (
            <PodsSection
              pods={fleet.runpod.pods}
              templateImage={fleet.runpod.template_image}
            />
          ) : null}
          <div className="grid gap-4 lg:grid-cols-2">
            {fleet.runpod ? <RunPodSection runpod={fleet.runpod} /> : null}
            {fleet.pool_queue ? (
              <PoolQueueSection pool={fleet.pool_queue} />
            ) : null}
          </div>
          {fleet.notes.length > 0 ? (
            <ul className="list-disc pl-4 text-xs text-muted-foreground">
              {fleet.notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : (
        <UnavailableCard error={error} />
      )}
    </div>
  );
};

GpuFleetPage.path = "/gpu-fleet";
